/**
 * KOA Modal Component
 * Handles modal functionality for the KOA project
 * Provides methods to open, close and manage modal state
 */

class KoaModal {
  /**
   * Initialize the KOA Modal component
   * Sets up event listeners and modal elements
   */
  constructor() {
    this.modal = null;
    this.body = document.body;
    this.isInitialized = false;
    this.debugMode = window.location.hostname === 'localhost';

    // Retry initialization if DOM is not ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  /**
   * Initialize modal component
   * Sets up DOM references and event listeners
   */
  init() {
    try {
      this.modal = document.getElementById('koaModal');

      if (!this.modal) {
        if (this.debugMode) {
          console.warn(
            'KoaModal: Modal element #koaModal not found, retrying...'
          );
        }
        // Retry after a short delay
        setTimeout(() => this.init(), 100);
        return;
      }

      this.setupEventListeners();
      this.isInitialized = true;

      if (this.debugMode) {
        console.log('KoaModal: Successfully initialized');
      }
    } catch (error) {
      if (this.debugMode) {
        console.error('KoaModal init error:', error);
      }
    }
  }

  /**
   * Set up event listeners for modal functionality
   * Handles keyboard events and accessibility
   */
  setupEventListeners() {
    // Keyboard event listener for ESC key
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && this.isOpen()) {
        this.close();
      }
    });

    // Focus trap for accessibility
    this.modal.addEventListener('keydown', event => {
      if (event.key === 'Tab') {
        this.handleTabKey(event);
      }
    });
  }

  /**
   * Handle tab key for focus trapping
   * Ensures focus stays within modal when open
   */
  handleTabKey(event) {
    const focusableElements = this.modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement.focus();
        event.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement.focus();
        event.preventDefault();
      }
    }
  }

  /**
   * Open the modal
   * Shows modal and prevents body scrolling
   */
  open() {
    if (!this.isInitialized || !this.modal) {
      if (this.debugMode) {
        console.warn(
          'KoaModal: Cannot open - not initialized or modal not found'
        );
      }
      // Try to reinitialize
      this.init();
      return;
    }

    try {
      this.modal.style.display = 'flex';
      this.body.classList.add('modal-open');
      this.body.style.overflow = 'hidden';

      // Focus management for accessibility
      const closeButton = this.modal.querySelector('.koa-modal-close');
      if (closeButton) {
        setTimeout(() => closeButton.focus(), 100);
      }

      if (this.debugMode) {
        console.log('KoaModal: Modal opened successfully');
      }
    } catch (error) {
      if (this.debugMode) {
        console.error('KoaModal open error:', error);
      }
    }
  }

  /**
   * Close the modal
   * Hides modal and restores body scrolling
   */
  close() {
    if (!this.isInitialized || !this.modal) {
      if (this.debugMode) {
        console.warn(
          'KoaModal: Cannot close - not initialized or modal not found'
        );
      }
      return;
    }

    try {
      this.modal.style.display = 'none';
      this.body.classList.remove('modal-open');
      this.body.style.overflow = 'auto';

      if (this.debugMode) {
        console.log('KoaModal: Modal closed successfully');
      }
    } catch (error) {
      if (this.debugMode) {
        console.error('KoaModal close error:', error);
      }
    }
  }

  /**
   * Check if modal is currently open
   * Returns boolean indicating modal state
   */
  isOpen() {
    return this.modal && this.modal.style.display === 'flex';
  }

  /**
   * Toggle modal open/close state
   * Convenience method for modal state management
   */
  toggle() {
    this.isOpen() ? this.close() : this.open();
  }
}

// Create modal instance
const koaModal = new KoaModal();

/**
 * Global function to open KOA modal
 * Maintains backward compatibility with existing onclick handlers
 */
window.openKoaModal = () => {
  if (koaModal.debugMode) {
    console.log('Global openKoaModal called');
  }
  koaModal.open();
};

/**
 * Global function to close KOA modal
 * Maintains backward compatibility with existing onclick handlers
 */
window.closeKoaModal = () => {
  if (koaModal.debugMode) {
    console.log('Global closeKoaModal called');
  }
  koaModal.close();
};

export default KoaModal;
export { koaModal };
